import {
  Activity,
  Component,
  CoveragePlanType,
  IApiMessage
} from '@cms-max/medicare-api-sdk'
import { Event } from 'models/activity/v2'
import generator, { EventMapKeys } from './baseGenerator'

export const accountHomeGenerator = generator(({ i18n }) => {
  const ns = 'accountHome'
  interface GenericAccountHomeEventProps {
    linkUrl: string | undefined
    linkText: string | undefined
    elementId: string
  }

  return {
    genericAccountHomeLink: ({
      linkText,
      linkUrl,
      elementId
    }: GenericAccountHomeEventProps) => {
      return {
        core: {
          elementId,
          property: 'MAX',
          activity: Activity.LinkClick,
          component: Component.AccountHome,
          coreClickData: {
            linkUrl,
            linkText
          }
        }
      }
    },
    genericAccountHomeModalLink: ({
      linkUrl,
      linkText,
      elementId
    }: GenericAccountHomeEventProps) => {
      return {
        core: {
          elementId,
          property: 'MAX',
          activity: Activity.LinkClick,
          component: Component.AccountHomeModal,
          coreClickData: {
            linkUrl,
            linkText
          }
        }
      }
    },
    home_toasts_render: ({
      guid: messageId,
      profileId
    }: Pick<IApiMessage, 'guid' | 'profileId'>) => ({
      core: {
        activity: Activity.MessageRender,
        coreClickData: {
          messageId,
          profileId
        },
        property: 'MAX',
        component: Component.MessageCenter
      }
    }),
    // Toast message content link click
    home_toasts_link_click: (params: {
      linkText: string
      linkUrl: string
    }): Event => ({
      core: {
        property: 'MAX',
        activity: Activity.LinkClick,
        component: Component.MessageCenterContent,
        coreClickData: {
          linkText: params.linkText,
          linkUrl: params.linkUrl
        }
      }
    }),
    home_toasts_markRead: ({
      guid: messageId,
      profileId
    }: Pick<IApiMessage, 'guid' | 'profileId'>) => ({
      core: {
        activity: Activity.MessageRead,
        coreClickData: {
          messageId,
          profileId
        },
        property: 'MAX',
        component: Component.MessageCenter
      }
    }),
    accounthome_usingyourmedicarecov_click: (params: {
      linkText: string
      linkUrl: string
    }): Event => ({
      core: {
        property: 'MAX',
        activity: Activity.LinkClick,
        component: Component.AccountHome,
        elementId: 'accounthome_usingyourmedicarecov_click',
        coreClickData: {
          linkText: params.linkText,
          linkUrl: params.linkUrl
        }
      }
    }),
    accounthome_medicarecosts_click: (params: {
      linkText: string
      linkUrl: string
    }): Event => ({
      core: {
        property: 'MAX',
        activity: Activity.LinkClick,
        component: Component.AccountHome,
        elementId: 'accounthome_medicarecosts_click',
        coreClickData: {
          linkText: params.linkText,
          linkUrl: params.linkUrl
        }
      }
    }),
    accounthome_claimsappeals_click: (params: {
      linkText: string
      linkUrl: string
    }): Event => ({
      core: {
        property: 'MAX',
        activity: Activity.LinkClick,
        component: Component.AccountHome,
        elementId: 'accounthome_claimsappeals_click',
        coreClickData: {
          linkText: params.linkText,
          linkUrl: params.linkUrl
        }
      }
    }),
    accounthome_reportmedicarefraud_click: (params: {
      linkText: string
      linkUrl: string
    }): Event => ({
      core: {
        property: 'MAX',
        activity: Activity.LinkClick,
        component: Component.AccountHome,
        elementId: 'accounthome_reportmedicarefraud_click',
        coreClickData: {
          linkText: params.linkText,
          linkUrl: params.linkUrl
        }
      }
    }),
    accounthome_contactmedicare_click: (params: {
      linkText: string
      linkUrl: string
    }): Event => ({
      core: {
        property: 'MAX',
        activity: Activity.LinkClick,
        component: Component.AccountHome,
        elementId: 'accounthome_contactmedicare_click',
        coreClickData: {
          linkText: params.linkText,
          linkUrl: params.linkUrl
        }
      }
    }),
    accounthome_livechat_click: {
      core: {
        property: 'MAX',
        activity: Activity.ButtonClick,
        component: Component.AccountHome,
        elementId: 'accounthome_livechat_click',
        coreClickData: {
          linkText: i18n.t('talkToSomeone.links.liveChat', { ns })
        }
      }
    },
    accounthome_plancard_click: (params: {
      linkText?: string
      linkUrl?: string
      planType: CoveragePlanType | 'om'
    }): Event => ({
      core: {
        elementId: `accounthome_${params.planType
          .replace(/ |_/g, '')
          .toLowerCase()}_click`,
        property: 'MAX',
        activity: Activity.LinkClick,
        component: Component.AccountHome,
        coreClickData: {
          linkText: params.linkText,
          linkUrl: params.linkUrl
        }
      }
    }),
    accounthome_checkplansummary_click: (params: {
      linkText?: string
      linkUrl?: string
    }): Event => ({
      core: {
        elementId: 'accounthome_checkplansummary_click',
        property: 'MAX',
        activity: Activity.LinkClick,
        component: Component.AccountHome,
        coreClickData: {
          linkText: params.linkText,
          linkUrl: params.linkUrl
        }
      }
    }),
    accounthome_getplandetails_click: (params: {
      linkText?: string
      linkUrl?: string
    }): Event => ({
      core: {
        elementId: 'accounthome_getplandetails_click',
        property: 'MAX',
        activity: Activity.LinkClick,
        component: Component.AccountHome,
        coreClickData: {
          linkText: params.linkText,
          linkUrl: params.linkUrl
        }
      }
    }),
    accounthome_getstartedwithmedicare_click: (params: {
      linkText?: string
      linkUrl?: string
    }): Event => ({
      core: {
        elementId: 'accounthome_getstartedwithmedicare_click',
        property: 'MAX',
        activity: Activity.LinkClick,
        component: Component.AccountHome,
        coreClickData: {
          linkText: params.linkText,
          linkUrl: params.linkUrl
        }
      }
    }),
    accounthome_openplanhistory_click: (params: {
      linkText?: string
      linkUrl?: string
    }): Event => ({
      core: {
        elementId: 'accounthome_openplanhistory_click',
        property: 'MAX',
        activity: Activity.LinkClick,
        component: Component.AccountHome,
        coreClickData: {
          linkText: params.linkText,
          linkUrl: params.linkUrl
        }
      }
    }),
    accounthome_openmydeductibles_click: (params: {
      linkText?: string
      linkUrl?: string
    }): Event => ({
      core: {
        elementId: 'accounthome_openmydeductibles_click',
        property: 'MAX',
        activity: Activity.LinkClick,
        component: Component.AccountHome,
        coreClickData: {
          linkText: params.linkText,
          linkUrl: params.linkUrl
        }
      }
    })
  }
})

export type AccountHomeKeys = EventMapKeys<typeof accountHomeGenerator>

export default accountHomeGenerator
